import Dialog from '../../ui/modal/Dialog';
import React, {useMemo, useState} from 'react';
import {handleError, handleSuccess} from '../../utils/Apollo';
import {useMutation, useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import {DateTime} from 'luxon';
import {Contact, ContactFeed, Event, EventsFeed, QueryContactsArgs, QueryEventsArgs} from '../../schema';
import UICheckbox from '../../ui/form/UICheckbox';
import UIListBox from '../../ui/form/UIListBox';
import cn from 'classnames';
import {Combobox} from '@headlessui/react';
import {UsersIcon} from "@heroicons/react/solid";
import classNames from "classnames";
import IconCheck from "../icon/IconCheck";
import IconSearch from "../icon/IconSearch";
import {useDebounce} from "../../utils/useDebounce";

type Props = {
  contact: Contact
  show: boolean
  setShowDialog: (close: boolean) => void
}

interface QueryFeed {
  contacts: ContactFeed
}

export default function MergeDlg({contact, show, setShowDialog}: Props) {
  const [transferAttendee] = useMutation(mergeMutation);
  const [fromContactId, setFromContactId] = useState<string | undefined>(undefined);
  const [showError, setShowError] = useState(false);

  const [searchBy, setSearchBy] = useState<string>(contact.name);
  const denouncedSearchBy = useDebounce(searchBy, 500);

  const {data, loading, error} = useQuery<QueryFeed, Pick<QueryContactsArgs, "searchBy">>(query, {
    variables: {
      searchBy: denouncedSearchBy
    }
  });

  const onConfirmMerge = () => {
    if (!fromContactId) {
      setShowError(true);
      return;
    }
    transferAttendee({
      variables: {id: contact.id, fromContactId}
    }).then(() => {
      handleSuccess(`Contact has been merged`);
      window.location.reload();
      setShowDialog(false);
    }, (apolloError) => handleError(apolloError));
  }

  const filteredContacts: Contact[] = data?.contacts.contacts.filter(c => c.id !== contact.id) || [];

  const handleContactSelection = (selectedContactId: string) => {
    setShowError(false);
    setFromContactId(selectedContactId);
  }

  return (
    <Dialog show={show} actionBtnTitle="Merge" onClose={() => setShowDialog(false)}
            title={`Merge contact`} onConfirm={() => onConfirmMerge()} closeBtnTitle="Cancel">
      <>
        {loading && <div>Loading...</div>}
        {error && <div>{error.message}</div>}
        {data &&
          <>
            <Combobox onChange={handleContactSelection} value={fromContactId}>
              <div className="relative">
                <IconSearch className="pointer-events-none absolute top-2.5 left-2 h-5 w-5 text-gray"/>
                <Combobox.Input
                  className="w-full rounded-md border-gray pl-8 px-4 py-2.5 text-gray-900 placeholder-gray-500 focus:ring-0 sm:text-sm"
                  placeholder="Search..."
                  displayValue={() => searchBy}
                  onChange={(event) => setSearchBy(event.target.value)}
                />
              </div>

              {filteredContacts.length > 0 && (
                <Combobox.Options
                  static
                  className="max-h-72 scroll-py-2 overflow-y-auto my-4 text-sm text-gray-dark border-l border-r border-gray-light"
                >
                  {filteredContacts.map((fromContact) => (
                    <Combobox.Option
                      key={fromContact.id}
                      value={fromContact.id}
                      className={({active}) =>
                        classNames(
                          'cursor-pointer select-none pl-1.5 rounded-md py-2',
                          active && 'bg-blue-table-hover',
                        )
                      }
                    >
                      {fromContact.id === fromContactId && <IconCheck className="text-blue mr-1"/>}
                      <span
                        className={classNames({
                          "ml-5": fromContact.id !== fromContactId,
                          "text-black": fromContact.id === fromContactId
                        })}>{fromContact.name}, {fromContact.email}</span>
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              )}

              {searchBy !== '' && filteredContacts.length === 0 && (
                <div className="py-14 px-4 text-center sm:px-14">
                  <UsersIcon className="mx-auto h-6 w-6 text-gray" aria-hidden="true"/>
                  <p className="mt-4 text-sm text-gray">No contacts found using that search term.</p>
                </div>
              )}
            </Combobox>
            <span className={cn('text-red text-sm', {'hidden': !showError})}>Select a contact for merging</span>
          </>
        }
      </>
    </Dialog>
  )
}

const query = gql`
  query SelectContacts($searchBy: String!) {
    contacts: contacts(skip: 0, count: 10, searchBy: $searchBy, orderBy: name_desc) {
      contacts {
        id
        name
        email
      }
    }
  }
`

const mergeMutation = gql`
  mutation MergeMutation($id: ID!, $fromContactId: ID!) {
    mergeContact(to: $id, from: $fromContactId) {
      id
    }
  }
`;
